import { z } from 'zod'
import { required } from '@portal/pkg-hook-form'

/**
 * A null scope on a scoped role tells the server to retain the persisted values. This lets a
 * metadata edit remain safe if an older response cannot supply the configured Entity Type IDs.
 *
 * The lengths match the legacy `role` table's columns, restated in `RoleLimits` on the server.
 */
export const NAME_MAX = 255
export const DESCRIPTION_MAX = 255

export const RoleSchema = z
  .object({
    /**
     * `superRefine(required())` rather than `min(1)` so that a blank box reads as the shared
     * "Required" message every other form in this app shows, and so that a name of nothing but
     * spaces is refused the way the server's `@NotBlank` refuses it.
     */
    name: z
      .string()
      .transform((value) => value.trim())
      .superRefine(required())
      .pipe(z.string().max(NAME_MAX)),
    /**
     * Optional, and blank means blank: the server treats an omitted description as a clear rather
     * than as "leave it alone", so an emptied box has to reach it as an empty string.
     */
    description: z.string().max(DESCRIPTION_MAX),
    entityTypeAllAccess: z.boolean(),
    entityTypeIds: z.array(z.number()).nullable(),
  })
  .superRefine((role, context) => {
    // Null on an existing scoped role means "retain the stored scope". An explicit empty
    // selection is always invalid and can never broaden the role to all Entity Types.
    if (!role.entityTypeAllAccess && role.entityTypeIds?.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityTypeIds'],
        message: 'roles.form.entityTypesRequired',
      })
    }
  })

export type RoleFormValues = z.infer<typeof RoleSchema>

export const roleDefaults: RoleFormValues = {
  name: '',
  description: '',
  entityTypeAllAccess: true,
  entityTypeIds: null,
}
